// Whales-Tab — Hyperliquid Whale-Alerts mit „Frag EVE dazu" CTA pro Item.
// Liest aus useWhaleAlertsStore (Live-Feed, wird vom AppShell befüllt).
//
// Klicks:
//   - Wallet-Adresse → HL-Explorer (neuer Tab)
//   - „Frag EVE" → askEve mit Pre-Filled-Prompt (kein Prop-Pipe, direkter
//     Drawer-Store-Zugriff)
//   - „🔔 Alarm" Toggle oben → browser-notifications bei neuen Whales
//     (Doc-Anforderung 2026-05-16, V1-Pendant)

import { Bell, BellOff, ExternalLink } from 'lucide-react';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import { selectWhaleAlerts, useWhaleAlertsStore } from '@/stores/whale-alerts-store';
import { useWhaleNotifier } from '@/hooks/use-whale-notifier';
import type { WhaleAlert } from '@/types/hyperliquid';

const HL_EXPLORER_BASE = 'https://app.hyperliquid.xyz/explorer/address';

export function TabWhales() {
  const alerts = useWhaleAlertsStore(selectWhaleAlerts);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const { enabled, supported, permission, toggle } = useWhaleNotifier();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[12px] uppercase tracking-wider text-neutral-500">
          Whales · Hyperliquid
        </span>
        <div className="flex items-center gap-2">
          {alerts.length > 0 ? (
            <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[11px] text-rose-300">
              {alerts.length}
            </span>
          ) : null}
          {supported ? (
            <button
              type="button"
              onClick={toggle}
              className={
                enabled
                  ? 'flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-300'
                  : 'flex items-center gap-1 rounded border border-white/10 px-1.5 py-0.5 text-[11px] text-neutral-400 hover:text-neutral-100'
              }
              title={
                permission === 'denied'
                  ? 'Browser-Benachrichtigungen sind blockiert — in den Browser-Einstellungen zulassen'
                  : enabled
                    ? 'Klick: Whale-Alarm aus'
                    : 'Klick: Whale-Alarm an (Browser fragt nach Berechtigung)'
              }
            >
              {enabled ? <Bell className="size-2.5" /> : <BellOff className="size-2.5" />}
              {enabled ? 'Alarm' : 'Alarm aus'}
            </button>
          ) : null}
        </div>
      </div>

      {alerts.length === 0 ? (
        <div className="rounded-lg border border-white/5 p-3 text-center text-xs text-neutral-500">
          Noch keine Whale-Alerts. WebSocket lauscht ab ≥$1M.
        </div>
      ) : (
        alerts.slice(0, 10).map((alert) => (
          <WhaleRow
            key={`${alert.time}-${alert.coin}-${alert.wallet ?? alert.hash ?? ''}`}
            alert={alert}
            onAskEve={(a) =>
              askEve(
                `${a.side} ${a.coin} · $${(a.sizeUsd / 1_000_000).toFixed(1)}M von ${
                  a.wallet ? shortAddr(a.wallet) : 'unbekannter Wallet'
                }\nWas steckt dahinter? Recherchiere News + Position-Kontext und beurteile ob das Signal oder Noise ist.`,
                {
                  displayText: `${a.side} ${a.coin} · $${(a.sizeUsd / 1_000_000).toFixed(1)}M von ${
                    a.wallet ? shortAddr(a.wallet) : 'unbekannter Wallet'
                  }`,
                },
              )
            }
          />
        ))
      )}
    </div>
  );
}

function WhaleRow({
  alert,
  onAskEve,
}: {
  alert: WhaleAlert;
  onAskEve: (a: WhaleAlert) => void;
}) {
  const isBuy = alert.side === 'BUY';
  const sideColor = isBuy ? 'text-emerald-300' : 'text-rose-300';
  const borderColor = isBuy ? 'border-emerald-500' : 'border-rose-500';
  const usd = `$${(alert.sizeUsd / 1_000_000).toFixed(1)}M`;
  const explorerUrl = alert.wallet ? `${HL_EXPLORER_BASE}/${alert.wallet}` : null;

  return (
    <div className={`glass rounded-lg border-l-2 p-2.5 ${borderColor}`}>
      <div className="mb-0.5 flex justify-between">
        <span className={`font-mono text-sm ${sideColor}`}>
          {alert.side} {alert.coin}
        </span>
        <span className="tabular text-neutral-300">{usd}</span>
      </div>
      <div className="mb-1.5 flex items-center justify-between text-[11px] text-neutral-500">
        {explorerUrl ? (
          <a
            href={explorerUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-0.5 font-mono hover:text-amber-300 hover:underline"
            aria-label={`Wallet ${alert.wallet} im HL-Explorer öffnen`}
          >
            {shortAddr(alert.wallet)}
            <ExternalLink className="size-2.5" />
          </a>
        ) : (
          <span className="font-mono">—</span>
        )}
        <span>{formatAgo(alert.time)}</span>
      </div>
      <button
        type="button"
        onClick={() => onAskEve(alert)}
        className="w-full rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-300 transition hover:bg-amber-500/20"
      >
        → Frag EVE dazu
      </button>
    </div>
  );
}

function shortAddr(addr: string | null): string {
  if (!addr || addr.length < 8) return '—';
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}

function formatAgo(timestamp: number): string {
  const sec = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (sec < 60) return `vor ${sec}s`;
  if (sec < 3600) return `vor ${Math.round(sec / 60)}min`;
  return `vor ${Math.round(sec / 3600)}h`;
}
